import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Dialog, DialogTitle, useToast } from '@mbbe/ui';
import { Clock3, ShieldCheck, Volume2 } from 'lucide-react';
import { confirmOpsContinuation, finishOpsTimer, getOpsActiveTimer, getOpsHeartbeat, sendOpsActivity, type OpsActiveTimer } from '../lib/opsApi';
import { continuationAlarm } from '../lib/continuationAlarm';
import { captureOpsMutationOwner, isStaleMutationError } from '../lib/mutationOwner';
import {
  canNotify,
  PERMISSION_CHANGE_EVENT,
  closeContinuationNotification,
  showContinuationNotification,
} from '../lib/desktopNotifications';
import { playContinuationVoice, stopContinuationVoice } from '../lib/continuationVoice';

const ALARM_ESCALATION_DELAY_MS = 10_000;
const ACTIVITY_HEARTBEAT_MS = 60_000;

import {
  safeGetItem,
  safeSetItem,
  safeRemoveItem,
  safeKeys,
  getSkew,
  broadcastOpsSync,
  isOpsSyncForCurrentSession,
  isValidTabActivity,
  readFinishNoteDraft,
  removeFinishNoteDraft,
} from '../lib/storage';
import { OPS_ACTIVE_TIMER_QUERY_KEY, resolveTransitionOutcome } from '../lib/timerState';

function secondsUntil(deadlineAt: string, clientNow: number, serverClockSkew: number) {
  return Math.max(0, Math.ceil((new Date(deadlineAt).getTime() - (clientNow + serverClockSkew)) / 1000));
}

export function ContinuationPrompt() {
  const queryClient = useQueryClient();
  const toast = useToast();
  // Keep the client clock and the server correction separate.  The correction
  // is applied exactly once when deriving server-relative timestamps.
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [serverClockSkew, setServerClockSkew] = useState(() => getSkew());
  const [soundBlocked, setSoundBlocked] = useState(false);
  const [isActivatingSound, setIsActivatingSound] = useState(false);
  // After a failed finish the button stays locked until the authoritative timer read settles.
  const [isVerifyingFinish, setIsVerifyingFinish] = useState(false);
  const [unverifiedFinishId, setUnverifiedFinishId] = useState<string | null>(null);
  const tabId = useMemo(() => Math.random().toString(36).substring(2, 9), []);
  const lastPhysicalInteractionAtRef = useRef(Date.now());
  const countdownId = useId();
  const taskLineId = useId();
  const confirmRef = useRef<HTMLButtonElement>(null);

  const heartbeatQuery = useQuery({
    queryKey: ['ops-heartbeat'],
    queryFn: getOpsHeartbeat,
    refetchInterval: 10_000,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    retry: 0,
  });



  useEffect(() => {
    if (heartbeatQuery.data?.serverNow) {
      const skew = new Date(heartbeatQuery.data.serverNow).getTime() - Date.now();
      setServerClockSkew(skew);
      safeSetItem('ops_server_time_skew', String(skew));
    }
  }, [heartbeatQuery.data?.serverNow]);

  const writeActivity = () => {
    try {
      const isFocused = document.hasFocus() && (Date.now() - lastPhysicalInteractionAtRef.current < 60_000);
      const activity = {
        tabId,
        updatedAt: Date.now(),
        visible: document.visibilityState === 'visible',
        focused: isFocused,
      };
      safeSetItem(`tab_activity_${tabId}`, JSON.stringify(activity));
    } catch (e) {
      console.error('Error writing activity:', e);
    }
  };

  const getLeaderAndAggregatedState = () => {
    const now = Date.now();
    const activeTabs: any[] = [];
    const keys = safeKeys();
    for (const key of keys) {
      if (key.startsWith('tab_activity_')) {
        const raw = safeGetItem(key);
        if (raw) {
          try {
            const act = JSON.parse(raw);
            if (isValidTabActivity(act) && now - act.updatedAt <= 15_000 && now - act.updatedAt >= -5_000) {
              activeTabs.push(act);
            }
          } catch {}
        }
      }
    }

    const isFocused = document.hasFocus() && (now - lastPhysicalInteractionAtRef.current < 60_000);
    const ourActivity = {
      tabId,
      updatedAt: now,
      visible: document.visibilityState === 'visible',
      focused: isFocused,
    };

    if (!activeTabs.some((t) => t.tabId === tabId)) {
      activeTabs.push(ourActivity);
    }

    const sortedTabs = [...activeTabs].sort((a, b) => {
      if (a.focused !== b.focused) return a.focused ? -1 : 1;
      if (a.visible !== b.visible) return a.visible ? -1 : 1;
      if (a.updatedAt !== b.updatedAt) return b.updatedAt - a.updatedAt;
      return a.tabId.localeCompare(b.tabId);
    });
    const leaderTab = sortedTabs[0];
    const electedLeader = leaderTab ? leaderTab.tabId === tabId : true;

    // Bypass localStorage trust ONLY if this tab is actually focused
    // We do not bypass for visible-but-unfocused tabs to prevent concurrent "focused: false" downgrades
    const isLeader = ourActivity.focused || electedLeader;

    const visible = ourActivity.visible;
    const focused = ourActivity.focused;

    return { isLeader, visible, focused };
  };

  useEffect(() => {
    const handleActivity = () => {
      lastPhysicalInteractionAtRef.current = Date.now();
    };
    window.addEventListener('mousemove', handleActivity, { passive: true });
    window.addEventListener('keydown', handleActivity, { passive: true });
    window.addEventListener('click', handleActivity, { passive: true });
    window.addEventListener('scroll', handleActivity, { passive: true });
    return () => {
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('click', handleActivity);
      window.removeEventListener('scroll', handleActivity);
    };
  }, []);

  useEffect(() => {
    writeActivity();
    const interval = window.setInterval(writeActivity, 5000);
    const handleEvents = () => {
      writeActivity();
    };
    document.addEventListener('visibilitychange', handleEvents);
    window.addEventListener('focus', handleEvents);
    window.addEventListener('blur', handleEvents);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', handleEvents);
      window.removeEventListener('focus', handleEvents);
      window.removeEventListener('blur', handleEvents);
      safeRemoveItem(`tab_activity_${tabId}`);
    };
  }, [tabId]);

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const channel = new BroadcastChannel('mbbe-timer-sync');
    channel.onmessage = (event) => {
      if (isOpsSyncForCurrentSession(event.data)) {
        invalidateOpsData();
      }
    };
    return () => {
      channel.close();
    };
  }, []);

  const prompt = heartbeatQuery.data?.continuationPrompt ?? null;
  const activeTimer = heartbeatQuery.data?.activeTimer ?? null;
  // Check the server's absolute deadline as well as polling. Background timers can
  // be suspended; focus/visibility events catch up as soon as the browser resumes.
  const refreshedCheckpoint = useRef<string | null>(null);
  const refetchHeartbeat = heartbeatQuery.refetch;
  useEffect(() => {
    if (!activeTimer?.nextCheckAt || prompt) return undefined;
    const deadline = new Date(activeTimer.nextCheckAt).getTime();
    if (!Number.isFinite(deadline)) return undefined;
    const key = `${activeTimer.id}:${activeTimer.nextCheckAt}`;
    const refreshIfDue = () => {
      if (Date.now() + serverClockSkew < deadline || refreshedCheckpoint.current === key) return;
      refreshedCheckpoint.current = key;
      void refetchHeartbeat();
    };
    const timer = window.setTimeout(refreshIfDue, Math.max(0, deadline - Date.now() - serverClockSkew));
    document.addEventListener('visibilitychange', refreshIfDue);
    window.addEventListener('focus', refreshIfDue);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', refreshIfDue);
      window.removeEventListener('focus', refreshIfDue);
    };
  }, [activeTimer?.id, activeTimer?.nextCheckAt, prompt?.id, serverClockSkew, refetchHeartbeat]);

  const remainingSeconds = useMemo(
    () => (prompt ? secondsUntil(prompt.deadlineAt, nowTick, serverClockSkew) : 0),
    [prompt, nowTick, serverClockSkew]
  );

  const invalidateOpsData = () => {
    queryClient.invalidateQueries({ queryKey: OPS_ACTIVE_TIMER_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: ['ops-dashboard'] });
    queryClient.invalidateQueries({ queryKey: ['ops-tasks'] });
    queryClient.invalidateQueries({ queryKey: ['ops-task'] });
    queryClient.invalidateQueries({ queryKey: ['ops-earnings'] });
    queryClient.invalidateQueries({ queryKey: ['ops-heartbeat'] });
  };

  const confirmMutation = useMutation({
    mutationFn: () => confirmOpsContinuation({ timerId: prompt?.workSessionId || '', checkInId: prompt?.id || '' }),
    onMutate: captureOpsMutationOwner,
    onSuccess: (_data, _vars, owner) => {
      if (!owner?.isCurrent()) return;
      toast.success({ title: 'Onaylandı', description: 'Çalışmaya devam ettiğin kaydedildi.' });
      broadcastOpsSync();
    },
    onError: (err, _vars, owner) => {
      if (isStaleMutationError(err, owner)) return;
      toast.error({ title: 'Başarısız', description: 'Süre dolmuş olabilir. Oturum durumu yenileniyor.' }, err);
    },
    onSettled: (_data, _err, _vars, owner) => {
      if (owner?.isCurrent()) invalidateOpsData();
    },
  });

  // The timer id is captured when the user presses finish; a delayed response then only cleans up
  // that session's own draft and can never touch a newer session's finish note.
  const finishMutation = useMutation({
    mutationFn: (vars: { timerId: string }) => {
      const note = readFinishNoteDraft(vars.timerId) || undefined;
      return finishOpsTimer({ timerId: vars.timerId, note });
    },
    // Pin the account at click time (the timer id is the mutation variable): a late response must
    // clear that account's draft for that timer, never whatever is rendered afterwards.
    onMutate: () => captureOpsMutationOwner(),
    onSuccess: (_log, vars, owner) => {
      removeFinishNoteDraft(vars.timerId, owner?.uid ?? null);
      if (!owner?.isCurrent()) return;
      continuationAlarm.stop();
      toast.success({ title: 'Bitirildi', description: 'Çalışma kaydı tamamlandı.' });
      invalidateOpsData();
      broadcastOpsSync();
    },
    onError: (err, vars, owner) => {
      if (isStaleMutationError(err, owner)) return;
      toast.error({ title: 'Başarısız', description: (err as Error).message }, err);
      // The response may have been lost after the server committed; read the real state back and
      // only offer the finish button again once that read says the finish did not commit.
      void reconcileFinish(vars.timerId);
    },
  });

  const reconcileFinish = async (timerId: string) => {
    // The read-back belongs to the account that pressed finish; a session change abandons it.
    const owner = captureOpsMutationOwner();
    setIsVerifyingFinish(true);
    try {
      const authoritative = await queryClient.fetchQuery({
        queryKey: OPS_ACTIVE_TIMER_QUERY_KEY,
        queryFn: ({ signal }) => getOpsActiveTimer(signal),
        staleTime: 0,
      });
      if (!owner.isCurrent()) return;
      setUnverifiedFinishId(null);
      if (resolveTransitionOutcome({ kind: 'finish', timerId }, authoritative) === 'committed') {
        removeFinishNoteDraft(timerId, owner.uid);
        continuationAlarm.stop();
        toast.success({ title: 'Doğrulandı', description: 'Çalışma sunucuda bitirilmiş; durum güncellendi.' });
        broadcastOpsSync();
      }
    } catch {
      if (owner.isCurrent()) setUnverifiedFinishId(timerId);
    } finally {
      setIsVerifyingFinish(false);
      if (owner.isCurrent()) invalidateOpsData();
    }
  };

  // A pending verification belongs to one timer; drop it once another (or no) timer is displayed.
  useEffect(() => {
    setUnverifiedFinishId((current) => (current && current !== activeTimer?.id ? null : current));
  }, [activeTimer?.id]);

  // The heartbeat already returns the running timer every 10s. When it disagrees with the cached
  // authoritative read (another device finished or switched it), re-read once; no extra polling.
  useEffect(() => {
    if (!heartbeatQuery.data) return;
    const cached = queryClient.getQueryData<OpsActiveTimer | null>(OPS_ACTIVE_TIMER_QUERY_KEY);
    if (cached === undefined) return;
    const fromHeartbeat = heartbeatQuery.data.activeTimer;
    if ((fromHeartbeat?.id ?? null) !== (cached?.id ?? null) || (fromHeartbeat?.taskId ?? '') !== (cached?.taskId ?? '')) {
      queryClient.invalidateQueries({ queryKey: OPS_ACTIVE_TIMER_QUERY_KEY });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heartbeatQuery.dataUpdatedAt]);

  const activateSound = async () => {
    setIsActivatingSound(true);
    setSoundBlocked(false);
    try {
      const ok = await continuationAlarm.unlock();
      setSoundBlocked(!ok);
      if (ok && prompt) {
        await continuationAlarm.start(prompt.id, { delayMs: 0 });
      }
    } catch {
      setSoundBlocked(true);
    } finally {
      setIsActivatingSound(false);
    }
  };

  useEffect(() => {
    const timer = window.setInterval(() => setNowTick(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!prompt || remainingSeconds > 0) return;
    continuationAlarm.stop();
    const timer = window.setTimeout(() => {
      heartbeatQuery.refetch().then(invalidateOpsData);
    }, 250);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prompt?.id, remainingSeconds <= 0]);

  useEffect(() => {
    if (!prompt) {
      continuationAlarm.stop();
      stopContinuationVoice();
      closeContinuationNotification();
      return undefined;
    }
    setSoundBlocked(false);

    let cancelled = false;
    let notified = false;

    const maybeShowDesktopNotification = () => {
      if (cancelled || notified || !canNotify()) return;
      // A checkpoint is an alarm even while Ops is focused. Retry if browser
      // permission is granted while this prompt is already open.
      notified = showContinuationNotification() !== null;
    };

    maybeShowDesktopNotification();
    document.addEventListener('visibilitychange', maybeShowDesktopNotification);
    window.addEventListener('blur', maybeShowDesktopNotification);
    window.addEventListener(PERMISSION_CHANGE_EVENT, maybeShowDesktopNotification);

    const elapsedMs = Math.max(0, (Date.now() + serverClockSkew) - new Date(prompt.promptedAt).getTime());
    const delayMs = Math.max(0, ALARM_ESCALATION_DELAY_MS - elapsedMs);

    // TTS primary (Gemini via backend + localStorage cache); sine is fallback only.
    playContinuationVoice()
      .then((played) => {
        if (cancelled) return;
        if (played) {
          setSoundBlocked(false);
          return;
        }
        return continuationAlarm.start(prompt.id, { delayMs }).then((ok) => {
          if (!cancelled) setSoundBlocked(!ok);
        });
      })
      .catch(() => {
        if (cancelled) return;
        return continuationAlarm.start(prompt.id, { delayMs })
          .then((ok) => {
            if (!cancelled) setSoundBlocked(!ok);
          })
          .catch(() => {
            if (!cancelled) setSoundBlocked(true);
          });
      });

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', maybeShowDesktopNotification);
      window.removeEventListener('blur', maybeShowDesktopNotification);
      window.removeEventListener(PERMISSION_CHANGE_EVENT, maybeShowDesktopNotification);
      continuationAlarm.stop();
      stopContinuationVoice();
      closeContinuationNotification();
    };
  }, [prompt?.id]);

  useEffect(() => {
    if (!activeTimer) return undefined;
    let cancelled = false;
    const sendActivity = () => {
      if (cancelled) return;
      writeActivity();
      const { isLeader, visible, focused } = getLeaderAndAggregatedState();
      if (!isLeader) return;
      sendOpsActivity({
        timerId: activeTimer.id,
        visible,
        focused,
        clientNow: new Date().toISOString(),
      }).catch(() => {
        // Network failures are intentionally silent; stale activity becomes a risk signal server-side.
      });
    };
    sendActivity();
    const intervalId = window.setInterval(sendActivity, ACTIVITY_HEARTBEAT_MS);
    document.addEventListener('visibilitychange', sendActivity);
    window.addEventListener('focus', sendActivity);
    window.addEventListener('blur', sendActivity);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', sendActivity);
      window.removeEventListener('focus', sendActivity);
      window.removeEventListener('blur', sendActivity);
    };
  }, [activeTimer?.id, tabId]);

  if (!prompt || !activeTimer) return null;

  const showSoundAction = soundBlocked || continuationAlarm.getStatus() !== 'ready';

  // The prompt blocks the page and has a hard deadline, so it is a modal alert dialog: it has a name, focus moves
  // to the answer action when it opens, Tab stays inside it, the page behind is inert and focus returns to the
  // previous control afterwards. It cannot be dismissed (Escape and backdrop do nothing): only an answer closes it.
  return createPortal(
    <Dialog
      open
      role="alertdialog"
      onClose={() => undefined}
      dismissible={false}
      initialFocusRef={confirmRef}
      aria-describedby={`${taskLineId} ${countdownId}`}
      className="z-[100] flex items-center justify-center p-4"
      backdropClassName="absolute inset-0 bg-slate-950/80 backdrop-blur-sm"
    >
      <div className="relative w-full max-w-sm rounded-lg border border-brand-500/30 bg-slate-900 p-6 shadow-2xl shadow-black/40">
        <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-300">Devam kontrolü</p>
          <DialogTitle className="mt-2 text-xl font-black tracking-tight text-white">Çalışmaya devam ediyor musun?</DialogTitle>
          <p id={taskLineId} className="mt-2 text-sm text-slate-400">
            {activeTimer.taskTitle || 'Genel çalışma'} oturumunu sürdürmek için süre dolmadan onay ver.
          </p>
        </div>
        <div className="flex h-11 w-11 items-center justify-center rounded-md border border-brand-500/20 bg-brand-500/10 text-brand-200">
          <ShieldCheck size={22} aria-hidden="true" />
        </div>
      </div>

      <div className="my-4 rounded-lg border border-slate-700 bg-slate-950/70 p-3 text-center">
        <div className="flex items-center justify-center gap-2 text-sm text-slate-400">
          <Clock3 size={16} aria-hidden="true" />
          Kalan süre
        </div>
        <div id={countdownId} className="mt-1 text-4xl font-black text-white tabular-nums">{remainingSeconds}s</div>
      </div>

      {showSoundAction && (
        <div className="mb-3 rounded-md border border-yellow-500/20 bg-yellow-500/10 px-3 py-2 text-xs text-yellow-100">
          <div className="flex flex-col gap-2">
            <span>Ses bu sekmede hazır değil. Zili duymak için sesi tekrar hazırla.</span>
            <Button variant="outline" size="sm" onClick={activateSound} isLoading={isActivatingSound} className="w-full">
              <Volume2 size={12} className="mr-1.5" />
              Sesi tekrar hazırla
            </Button>
          </div>
        </div>
      )}

      <div className="flex gap-3">
        <Button
          ref={confirmRef}
          variant="primary"
          className="flex-1"
          isLoading={confirmMutation.isPending}
          disabled={remainingSeconds <= 0}
          onClick={(event) => {
            if (import.meta.env.MODE !== 'test' && event.isTrusted !== true) return;
            continuationAlarm.stop();
            confirmMutation.mutate();
          }}
        >
          Devam Ediyorum
        </Button>
        <Button
          variant="danger"
          className="flex-1"
          isLoading={finishMutation.isPending || isVerifyingFinish}
          disabled={remainingSeconds <= 0 || finishMutation.isPending || isVerifyingFinish}
          onClick={() => {
            if (unverifiedFinishId) {
              void reconcileFinish(unverifiedFinishId);
              return;
            }
            finishMutation.mutate({ timerId: activeTimer.id });
          }}
        >
          {unverifiedFinishId ? 'Sonucu doğrula' : 'Çalışmayı Bitir'}
        </Button>
      </div>
      </div>
    </Dialog>,
    document.body
  );
}
